import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { HardDrive, Loader2, CloudOff, Database, Boxes } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { PLAN_CLS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

const mb = (b) => ((b || 0) / 1048576).toFixed(1);

export default function StorageMigration() {
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/all-media", { params: { scope } })
      .then((r) => setState(r.data))
      .catch(() => setState({ service_available: false, tenants: [], totals: {} }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const tenants = state.tenants || [];
  const totals = state.totals || {};

  return (
    <div data-testid="storage-page">
      <PageHeader title="Storage & Migration" subtitle="Objekt-Storage-Nutzung je FUNDIO-Kunde aus der kanonischen Media-API (read-only)." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span data-testid="storage-readonly" className="inline-flex items-center rounded-full border border-fundio-border bg-fundio-subtle px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-fundio-muted">READ ONLY</span>
        <span data-testid="storage-nocutover" className="inline-flex items-center rounded-full border border-status-danger-border bg-status-danger-bg px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-status-danger">NO CUTOVER</span>
      </div>

      {!state.service_available ? (
        <div data-testid="storage-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div data-testid="storage-provider" className="card-surface rounded-xl p-5"><div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wider text-fundio-muted"><Database className="h-3.5 w-3.5" />Provider</div><div className="font-display text-lg font-medium text-fundio-ink">Objekt-Storage</div><div className="text-xs text-fundio-faint">Pfad-Präfix fundio/…</div></div>
            <div className="card-surface rounded-xl p-5"><div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wider text-fundio-muted"><Boxes className="h-3.5 w-3.5" />Media Records</div><div className="font-display text-3xl font-bold text-fundio-ink">{totals.records ?? 0}</div></div>
            <div className="card-surface rounded-xl p-5"><div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wider text-fundio-muted"><HardDrive className="h-3.5 w-3.5" />Belegt</div><div className="font-display text-3xl font-bold text-fundio-ink">{mb(totals.total_bytes)}<span className="text-base font-normal text-fundio-muted"> MB</span></div></div>
            <div className="card-surface rounded-xl p-5"><div className="mb-1 text-xs uppercase tracking-wider text-fundio-muted">Quota (gesamt)</div><div className="font-display text-3xl font-bold text-fundio-ink">{mb(totals.quota_bytes)}<span className="text-base font-normal text-fundio-muted"> MB</span></div></div>
          </div>

          <Panel title={`Speicher je Kunde (${tenants.length})`} testid="storage-panel">
            {!tenants.length ? (
              <div className="p-6 text-sm text-fundio-muted">Keine Media-Daten im gewählten Bereich.</div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint">
                  <th className="px-2 py-2 font-medium">Kunde</th><th className="px-2 py-2 font-medium">Plan</th>
                  <th className="px-2 py-2 font-medium">Records</th><th className="px-2 py-2 font-medium">Belegt (MB)</th>
                  <th className="px-2 py-2 font-medium">Quota (MB)</th><th className="px-2 py-2 font-medium">Auslastung</th>
                </tr></thead>
                <tbody className="divide-y divide-fundio-border">
                  {tenants.map((t) => {
                    const pct = t.quota_bytes ? Math.min(100, Math.round((t.total_bytes / t.quota_bytes) * 100)) : 0;
                    return (
                      <tr key={t.tenant_id} data-testid={`storage-row-${t.tenant_id}`} className="hover:bg-fundio-subtle">
                        <td className="px-2 py-3 font-medium text-fundio-ink">{t.company_name || t.tenant_id}{t.is_demo && <span className="ml-2 rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold text-status-warning">QA</span>}</td>
                        <td className="px-2 py-3">{t.plan ? <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[t.plan] || PLAN_CLS.FREE}`}>{t.plan}</span> : "—"}</td>
                        <td className="px-2 py-3 text-fundio-ink">{t.count}</td>
                        <td className="px-2 py-3 text-fundio-ink">{mb(t.total_bytes)}</td>
                        <td className="px-2 py-3 text-fundio-muted">{mb(t.quota_bytes)}</td>
                        <td className="px-2 py-3"><div className="flex items-center gap-2"><div className="h-1.5 w-24 overflow-hidden rounded-full bg-fundio-subtle"><div className="h-full rounded-full bg-fundio-gold" style={{ width: `${pct}%` }} /></div><span className="text-xs text-fundio-faint">{pct}%</span></div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
          </Panel>

          <div data-testid="storage-gap-note" className="mt-4 rounded-xl border border-status-warning-border bg-status-warning-bg/40 px-5 py-4 text-xs text-status-warning">
            <span className="font-semibold">GAP — noch nicht über CONTROL verfügbar:</span> Missing Objects, Zero-byte Objects, Unattached Media, R2-Bucket-Status und «SAFE FOR COPY» besitzen keinen kanonischen Platform-Admin-Endpoint. Es werden keine erfundenen Werte angezeigt. Copy / Delete / Cutover sind bewusst deaktiviert.
          </div>
        </>
      )}
    </div>
  );
}
